import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Typography, TextField, Grid, IconButton, List, ListItem, FormControlLabel, Radio, Tooltip } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";
import { getFileSize } from "../../../utils/file.js";
import { getFileTypeIcon } from "../../../utils/comment";
import { downloadFile, isImage, isFileSizeGreaterThan } from "../../../utils/file";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { createSafetyReportApi, uploadSafetyFileApi, downloadReportApi } from "../../../api/safety.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import Popup from "../../../component/popup/Popup.js";
import ConfirmPopup from "../../../component/popup/ConfirmPopup.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const ManageSafetyPopup = ({ open, onClose, readOnly, onAdded, data }) => {
  const allowedTypes = [
    "image/jpeg", "image/png", "image/gif", "image/bmp", "image/tiff", "image/webp",
    "application/pdf",
    "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ];
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const textFieldStyle = {
    my: 0.5,
    "& .MuiInputBase-input": {
      py: 0.5,
      px: 0,
    },
    "& .MuiInputBase-root": {
      px: 2,
      py: 0,
    },
  };
  const datePickerStyle = {
    flex: 1,
    borderRadius: "4px",
    background: "#fff",
    "& .MuiInputBase-input": {
      pl: 2,
      pr: 0,
      py: 0.5,
    },
    "& .MuiIconButton-root.Mui-disabled": {
      color: "transparent!important",
    },
    "& .MuiOutlinedInput-input.Mui-disabled": {
      WebkitTextFillColor: "#000",
    },
    "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent!important",
    },
  };
  const labelStyle = {
    color: "#333",
    fontWeight: 700,
    whiteSpace: "nowrap",
    minWidth: 130,
  };

  const [state, setState] = useState({
    date: dayjs(),
    time: "00:00",
    subject: "",
    location: "",
    alertNo: "",
    summary: "",
    actions: "",
    lessonLearnt: "",
    classificationList: [
      { value: 1, label: "SIF Potential" },
      { value: 2, label: "SIF Actual" },
    ],
    classification: 1,
    files: [],
  });
  const [fileUploads, setFileUploads] = useState([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalidNumber, setIsInvalidNumber] = useState(false);

  const isFormValid = () => {
    return (
      state.date !== "" &&
      state.time !== "" &&
      state.subject !== "" &&
      state.location !== "" &&
      state.summary !== "" &&
      state.actions !== "" &&
      state.lessonLearnt !== "" &&
      state.classification !== "" &&
      !isInvalidNumber
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const newFileUploads = Array.from(files).map((file) => ({
        name: file.name,
        filesize: getFileSize(file.size),
        file: file,
      }));
      setFileUploads([...fileUploads, ...newFileUploads]);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const maxSizeMB = 10;

    const newFileUploads = [];
    Array.from(files).forEach((file) => {
      const isValidSize = isFileSizeGreaterThan(file.size, maxSizeMB);
      if (isValidSize) {
        showError(`Upload Failed: File "${file.name}" exceeds the maximum size of ${maxSizeMB}MB.`);
        return;
      }
      if (!allowedTypes.includes(file.type)) {
        showError(`Upload Failed: File "${file.name}" type is not supported.`);
        return;
      }
      newFileUploads.push({
        name: file.name,
        filesize: getFileSize(file.size),
        file: file,
      });
    });
    if (newFileUploads.length > 0) {
      setFileUploads([...fileUploads, ...newFileUploads]);
    }
  };

  const removeFile = (index) => {
    const updatedFiles = fileUploads.filter((_, i) => i !== index);
    setFileUploads(updatedFiles);
  };

  const handleSubmit = async (e) => {
    setIsConfirming(false);
    setIsLoading(true);
    e.preventDefault();
    const dateOnly = dayjs(state.date).format("YYYY-MM-DD");
    const datetimeStr = `${dateOnly} ${state.time}`;
    const params = {
      datetime: dayjs(datetimeStr, "YYYY-MM-DD HH:mm"),
      subject: state.subject,
      location: state.location,
      alertNo: state.alertNo,
      classification: state.classification,
      summary: state.summary,
      actions: state.actions,
      lessonLearnt: state.lessonLearnt,
    };
    try {
      const result = await createSafetyReportApi(projectId, params);
      if (result?.data?.safetyReportId) {
        const reportId = result.data.safetyReportId;
        if (fileUploads.length > 0) {
          await Promise.all(
            fileUploads.map(async (f) => {
              const formData = new FormData();
              formData.append("file", f.file);
              await uploadSafetyFileApi(projectId, reportId, formData);
            })
          );
        }
        setState((prevState) => ({
          ...prevState,
          date: dayjs(),
          time: "00:00",
          subject: "",
          location: "",
          alertNo: "",
          summary: "",
          actions: "",
          lessonLearnt: "",
          classification: 1,
        }));
        setFileUploads([]);
        showSuccess("New Report Added.");
        onAdded();
      } else {
        showError("Add failed.");
      }
    } catch {
      showError("Add failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadReport = async () => {
    setIsLoading(true);
    try {
      const result = await downloadReportApi(projectId, data?.safetyReportId);
      if (result?.data) {
        const data = result.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      }
    } catch {
      showError("Download failed.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!data) return;
    setState(data);
  }, [data]);

  return (
    <Popup
      open={open}
      onClose={onClose}
      popupStyle={{
        minWidth: 600,
        maxWidth: 600,
      }}
      title={readOnly ? "Safety Report" : "Manage Safety"}
    >
      <Box
        sx={{
          pt: 2,
          pb: 1,
          px: 4,
          minHeight: "500px",
          maxHeight: "calc(100vh - 160px)",
          overflow: "auto",
        }}
      >
        <Grid container>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={labelStyle}>Incident Details</Typography>
          </Grid>
          <Grid item xs={6} sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography sx={labelStyle}>*Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                disabled={readOnly}
                value={state.date}
                onChange={(value) => setState((prevState) => ({ ...prevState, date: value }))}
                format="DD/MM/YYYY"
                inputFormat="DD/MM/YYYY"
                renderInput={(params) => <TextField {...params} />}
                sx={datePickerStyle}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                color: "#333",
                fontWeight: 700,
                minWidth: 130,
                textAlign: "right",
              }}
            >
              *Time
            </Typography>
            <TextField
              disabled={readOnly}
              id="time"
              type="time"
              value={state.time}
              onChange={(event) => {
                setState((prevState) => ({
                  ...prevState,
                  time: event.target.value,
                }));
              }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300,
              }}
              sx={{
                width: 163,
                ml: 1,
                "& .MuiInputBase-input": {
                  pl: 2,
                  py: 0.5,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography sx={labelStyle}>*Subject</Typography>
            <TextField
              disabled={readOnly}
              value={state.subject}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  subject: e.target.value,
                }))
              }
              fullWidth
              placeholder="Please Enter Subject..."
              variant="outlined"
              sx={textFieldStyle}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <Typography sx={labelStyle}>*Location</Typography>
            <TextField
              disabled={readOnly}
              value={state.location}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  location: e.target.value,
                }))
              }
              fullWidth
              placeholder="Please Enter Location..."
              variant="outlined"
              sx={textFieldStyle}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={labelStyle}>Safety Alert Number</Typography>
            <TextField
              disabled={true}
              value={state.alertNo}
              onChange={(e) => {
                setState((prevState) => ({
                  ...prevState,
                  alertNo: e.target.value,
                }));
                setIsInvalidNumber(!(!isNaN(e.target.value) && !isNaN(parseFloat(e.target.value))));
              }}
              fullWidth
              placeholder="Auto generated from Project Workplace"
              variant="outlined"
              sx={textFieldStyle}
            />
          </Grid>
          {isInvalidNumber && (
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "end" }}>
              <Typography
                sx={{
                  color: "red",
                  whiteSpace: "nowrap",
                }}
              >
                Invalid Safety Alert Number
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sx={{ display: "flex", alignItems: "center", my: 0.5 }}>
            <Typography sx={labelStyle}>*SIF Classification</Typography>
            {state.classificationList?.map((item) => {
              const isChecked = state.classification === item.value;
              return (
                <FormControlLabel
                  disabled={readOnly}
                  key={item.value}
                  control={
                    <Radio
                      checked={isChecked}
                      onChange={(event) => {
                        setState((prevState) => ({
                          ...prevState,
                          classification: Number(event.target.value),
                        }));
                      }}
                      value={item.value}
                      name="Classification"
                      color="primary"
                      sx={{ px: 0.5, py: 0.3 }}
                    />
                  }
                  label={item.label}
                  sx={{ minWidth: "110px", mr: 1, ml: "-6px" }}
                />
              );
            })}
          </Grid>
          {!readOnly && (
            <Grid item xs={12}>
              <Typography sx={labelStyle}>
                File Upload{" "}<span style={{ fontWeight: "normal" }}>(Max upload limit 10 MB. Accepted file types: Word, Excel, PDF and images)</span>
              </Typography>
              <Box
                sx={{ border: "1px #ccc dashed", p: 2, my: 0.5 }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <IconButton
                  component="label"
                  sx={{
                    borderRadius: 0,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  <input
                    type="file"
                    onChange={handleFileChange}
                    multiple
                    accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: "none" }}
                  />
                  <img src="assets/buttons/uploadfile.svg" alt="uploadfile"></img>
                  Click or drag to upload files
                </IconButton>
              </Box>
              <Typography sx={labelStyle}>Attached Files</Typography>
              <Box
                sx={{
                  height: "100px",
                  backgroundColor: "#F5F5F5",
                  borderRadius: "4px",
                  my: 0.5,
                  px: 2,
                  py: 1,
                  overflow: "auto",
                }}
              >
                {fileUploads.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {fileUploads.map((file, index) => (
                      <ListItem key={index} sx={{ display: "flex", alignItems: "center", p: 0 }}>
                        <img src="assets/icons/file.svg" alt="file" style={{ height: 20 }}></img>
                        <Typography variant="body1" sx={{ ml: 1, mr: 2 }}>
                          {file.name} ({file.filesize})
                        </Typography>
                        <HighlightOff onClick={() => removeFile(index)} sx={{ cursor: "pointer" }}></HighlightOff>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1">No files selected</Typography>
                )}
              </Box>
            </Grid>
          )}
          {readOnly && (
            <>
              <Grid item xs={12} sx={{ mb: 0.5 }}>
                <Typography sx={labelStyle}>&nbsp;&nbsp;Attached Files</Typography>
              </Grid>
              <Grid container>
                {state.files.length > 0 && (
                  <Grid item xs={12}>
                    <Typography>&nbsp;&nbsp;Click the file to download </Typography>
                  </Grid>
                )}
                {state.files?.map((file, index) => (
                  <Grid item xs={3} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                    <Button onClick={() => downloadFile(file)} sx={{ width: "100%", padding: 0 }}>
                      <Box sx={{ py: 0.5, textAlign: "center" }}>
                        <img
                          style={{ width: 47, height: 47 }}
                          alt={file.format}
                          src={
                            isImage(file.format) ? `${file?.fileURL}` : getFileTypeIcon(file.format)
                          }
                        />
                        <Tooltip title={file.filename + "." + file.format}>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#474747",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              width: "100px",
                            }}
                          >
                            {file.filename}.{file.format}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography sx={labelStyle}>*Incident Summary</Typography>
            <TextField
              disabled={readOnly}
              value={state.summary}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  summary: e.target.value,
                }))
              }
              multiline
              rows={4}
              fullWidth
              placeholder="Please Enter Incident Summary..."
              variant="outlined"
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={labelStyle}>*Immediate Actions Taken</Typography>
            <TextField
              disabled={readOnly}
              value={state.actions}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  actions: e.target.value,
                }))
              }
              multiline
              rows={4}
              fullWidth
              placeholder="Please Enter Immediate Actions Taken..."
              variant="outlined"
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={labelStyle}>*Lessons Learnt</Typography>
            <TextField
              disabled={readOnly}
              value={state.lessonLearnt}
              onChange={(e) =>
                setState((prevState) => ({
                  ...prevState,
                  lessonLearnt: e.target.value,
                }))
              }
              multiline
              rows={4}
              fullWidth
              placeholder="Please Enter Lessons Learnt..."
              variant="outlined"
              sx={textFieldStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <Box textAlign="center" my={1.5}>
              {!readOnly ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isFormValid()}
                  color={isFormValid() ? "success" : "primary"}
                  sx={{ borderRadius: "10px", lineHeight: "normal" }}
                  onClick={(event) => {
                    event.preventDefault();
                    setIsConfirming(true);
                  }}
                >
                  Submit
                </Button>
              ) : (
                <Button
                  onClick={downloadReport}
                  type="download"
                  variant="contained"
                  color="success"
                  sx={{ borderRadius: "10px", lineHeight: "normal" }}
                >
                  Download
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <ConfirmPopup
        open={isConfirming}
        message="Confirm to submit new report?"
        onCancel={() => setIsConfirming(false)}
        onConfirm={handleSubmit}
      ></ConfirmPopup>
      <FullScreenLoading isLoading={isLoading} />
    </Popup>
  );
};

export default ManageSafetyPopup;
